import { helpers } from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';

export const phoneNumberValidator = helpers.withMessage(
  'Please enter a valid US phone number',
  (value: string | null | undefined) => {
    if (!value) return true; // emptiness is enforced separately (requiredIf)
    if (typeof value !== 'string') return false;
    // Mirrors the backend's normalization + strict US check in
    // tallo-be/src/utils/parsing.utils.ts formatPhoneNumber, so numbers accepted
    // here cannot be rejected by the API later. Accepts 10 digits, 1XXXXXXXXXX
    // and +1XXXXXXXXXX (values pre-seeded from the API are stored in E.164).
    const normalized = value.replace(/[^+\d]/g, '');
    return isValidPhoneNumber(normalized, 'US');
  },
);

export const isNotFutureDateValidator = helpers.withMessage(
  'Date cannot be in the future',
  (value: string | undefined) => {
    if (!value) return true;
    return new Date(value) <= new Date();
  },
);

export const isNumberValidator = helpers.withMessage('Only numbers are allowed', (value: string | undefined) => {
  if (!value) return true;
  return /^\d+$/.test(value);
});

export const isAgeBetweenValidator = helpers.withMessage(
  'Age must be between 18 and 125 years',
  (value: string | undefined) => {
    if (!value) return true;

    const today = new Date();
    const inputDate = new Date(value);

    let age = today.getFullYear() - inputDate.getFullYear();
    const monthDiff = today.getMonth() - inputDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < inputDate.getDate())) {
      age--;
    }

    return age >= 18 && age <= 125;
  },
);

export const checkboxCheckedValidator = (message: string) => helpers.withMessage(message, (value: boolean) => value);

export const ssnMatchValidator = helpers.withMessage(
  'SSN numbers must match',
  (value: string, siblings: { nationalId: string }) => value === siblings.nationalId,
);

/**
 * Creates a validator that ensures end date is not before start date
 * @param startDateField - name of the field containing the start date
 * @returns validator function
 */
export const endDateValidator = (startDateField: string) =>
  helpers.withMessage(
    'End date cannot be before start date',
    (value: string | undefined, siblings: Record<string, string>) => {
      const startDate = siblings[startDateField];
      if (!value || !startDate) {
        return true;
      }

      return new Date(startDate) <= new Date(value);
    },
  );

/**
 * Validates that a name contains at least a first and last name
 * and doesn't contain special characters
 */
export const fullNameValidator = helpers.withMessage(
  'Please enter your full name (first and last name)',
  (value: string | undefined) => {
    if (!value) return true;

    // Trim the value and check if it's not empty after trimming
    const trimmedValue = value.trim();
    if (!trimmedValue) return true;

    // Check if name has at least two parts (first and last name)
    const nameParts = trimmedValue.split(/\s+/);
    if (nameParts.length < 2) return false;

    // Check if each part is at least 2 characters long
    if (nameParts.some((part) => part.length < 2)) return false;

    // Check if name contains only letters, spaces, hyphens, apostrophes, and dots (for suffixes like Jr.)
    return /^[A-Za-z\s'-.]+$/.test(trimmedValue);
  },
);
